import { effect, inject } from '@angular/core';
import { FileService, News, NewsService, UploadFile200Response } from '@api';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { pipe, switchMap, tap } from 'rxjs';
import { UserStore } from './user.store';

export interface NewStateInterface {
  news: News[];
  total: number;
  searchFilter: string;
  colSort: string;
  sortOrder: 'asc' | 'desc' | '';
  page: number;
  pageSize: number;
  criteria: string;
  loadingNews: boolean;
}

export const NewsStore = signalStore(
  withState<NewStateInterface>({
    news: [],
    total: 0,
    searchFilter: '',
    colSort: 'name',
    sortOrder: 'asc',
    page: 1,
    pageSize: 10,
    criteria: '',
    loadingNews: false,
  }),
  withMethods(
    (
      store,
      userStore = inject(UserStore),
      newsService = inject(NewsService),
      toastr = inject(ToastrService),
      fileService = inject(FileService)
    ) => ({
      addNews(n: News, file: Blob) {
        fileService
          .uploadImage(file)
          .subscribe((resp: UploadFile200Response) => {
            n.uuid = resp.uuid;
            newsService
              .saveNews(n)
              .subscribe(
                () =>
                  toastr.success(`L'actualité "${n.name}" a été créée`) &&
                  this.loadNews()
              );
          });
      },
      updateNews(n: News, file: Blob) {
        if (file) {
          fileService
            .uploadImage(file)
            .subscribe((resp: UploadFile200Response) => {
              n.uuid = resp.uuid;
              newsService
                .updateNews(n.id, n)
                .subscribe(
                  () =>
                    toastr.success(
                      `L'actualité "${n.name}" a été mise à jour`
                    ) && this.loadNews()
                );
            });
        } else {
          newsService
            .updateNews(n.id, n)
            .subscribe(
              () =>
                toastr.success(`L'actualité "${n.name}" a été mise à jour`) &&
                this.loadNews()
            );
        }
      },
      removeNew(news: News) {
        newsService
          .deleteNews(news.id)
          .subscribe(
            () =>
              toastr.success(`L'actualité "${news.name}" a été supprimée`) &&
              this.loadNews()
          );
      },
      pageEvent(
        colSort: string,
        sortOrder: 'asc' | 'desc' | '',
        page: number,
        pageSize: number,
        criteria: string = ''
      ) {
        patchState(store, { colSort, sortOrder, page, pageSize, criteria });
        this.loadNews();
      },
      loadNews: rxMethod<void>(
        pipe(
          switchMap(() => {
            patchState(store, { loadingNews: true });
            return newsService
              .getNews(
                userStore.contract()?.id,
                store.colSort(),
                store.sortOrder(),
                store.page(),
                store.pageSize(),
                store.searchFilter(),
                store.criteria()
              )
              .pipe(
                tap((page) => {
                  patchState(store, {
                    // @ts-ignore
                    news: page.results,
                    total: page.total,
                    loadingNews: false,
                  });
                })
              );
          })
        )
      ),
    })
  ),
  withHooks({
    onInit(store) {
      store.loadNews();
      const userStore = inject(UserStore);
      effect(
        () => {
          const contract = userStore.contract();
          store.loadNews();
        },
        { allowSignalWrites: true }
      );
    },
  })
);
